import { CriticalAlertIcon } from '@/components/critical-alert-icon/critical-alert-icon';
import {
  Button,
  Modal,
  ModalBody,
  ModalBodyProps,
  ModalCloseButton,
  ModalContent,
  ModalContentProps,
  ModalFooter,
  ModalFooterProps,
  ModalOverlay,
  Text,
  TextProps,
} from '@chakra-ui/react';
import { ReactNode, useCallback, useState } from 'react';
import { match } from 'ts-pattern';

export type AlertType = 'success' | 'error' | 'warning' | 'info';

export type AlertPopupProps = {
  alertType: AlertType;
  title: string;
  body: string | ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => Promise<void>;
  onCancel?: () => void;
  showCloseButton?: boolean;
  closePopup: () => void;
  bodyProps?: TextProps;
  modalFooterProps?: ModalFooterProps;
  modalBodyProps?: ModalBodyProps;
  modalContentProps?: ModalContentProps;
  hideCancelButton?: boolean;
};

export const AlertPopup = (props: AlertPopupProps) => {
  const {
    alertType,
    title,
    body,
    showCloseButton = false,
    closePopup,
    bodyProps,
    modalFooterProps,
    modalBodyProps,
    modalContentProps,
  } = props;
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const onCloseEvent = useCallback(() => {
    setIsOpen(false);
    closePopup();
  }, [setIsOpen, closePopup]);

  return (
    <Modal isOpen={isOpen} onClose={onCloseEvent} isCentered={true}>
      <ModalOverlay />
      <ModalContent {...modalContentProps}>
        {showCloseButton && <ModalCloseButton mt={'0.5rem'} mr={'1rem'} />}
        <ModalBody
          display={'flex'}
          flexDirection={'column'}
          alignItems={'center'}
          py={'2rem'}
          px={'1.5rem'}
          {...modalBodyProps}
        >
          {alertType === 'warning' && <CriticalAlertIcon />}
          <Text mt={'1rem'} mb={'0.75rem'} fontWeight={600} fontSize={'1rem'}>
            {title}
          </Text>
          <Text fontSize={'0.875rem'} {...bodyProps}>
            {body}
          </Text>
        </ModalBody>
        <ModalFooter
          gap={'0.5rem'}
          alignItems={'flex-start'}
          justifyContent={'space-between'}
          py={'0.875rem'}
          px={'1rem'}
          {...modalFooterProps}
        >
          {composeActions(props, onCloseEvent)}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// TODO: fill this progressively with the other alert types
const composeActions = (props: AlertPopupProps, onCloseEvent: () => void) => {
  const { alertType, cancelLabel, onCancel, confirmLabel, onConfirm } = props;

  return match(alertType)
    .with('success', 'error', 'info', () => null)
    .with('warning', () => (
      <>
        {onConfirm && (
          <Button
            size={'sm'}
            variant={'destructiveSolid'}
            flexGrow={1}
            onClick={() => {
              void onConfirm?.().then(() => {
                onCloseEvent();
              });
            }}
          >
            {confirmLabel || 'Confirm'}
          </Button>
        )}
        {!props.hideCancelButton && (
          <Button
            size={'sm'}
            variant={'secondaryOutline'}
            flexGrow={1}
            onClick={() => {
              onCancel?.();
              onCloseEvent();
            }}
          >
            {cancelLabel || 'Cancel'}
          </Button>
        )}
      </>
    ))
    .otherwise(() => (
      <Button
        size={'sm'}
        variant={'secondaryOutline'}
        flexGrow={1}
        onClick={() => {
          onCancel?.();
          onCloseEvent();
        }}
      >
        {cancelLabel || 'Close'}
      </Button>
    ));
};
